import React, { useState } from 'react';
import { Box, Flex } from 'rebass';
import styled, { css } from 'styled-components';
import Image from 'gatsby-image';
import capitalize from 'lodash/capitalize';
import CoreText from '../../Text';
import Html from '../../Layout/Html';
import PropertyPair from '../../Event/Card/PropertyPair';
import Button from '../../Button';
import Link from '../../Link';
import { boolFilters } from '../utils';
import usAndCanadaStates from '../../../utils/usAndCanadaStates';
import GraduateBadges from './Badges';
import avatarPlaceholder from './avatar-placeholder.svg';

const iframeRegExp = new RegExp(/(width|height)="(\d+)"/g);

const Text = styled(CoreText)`
  transition: 0.2s;
`;

export const Wrapper = styled(Box)`
  ${({ theme, single }) => css`
    border-bottom: ${single ? 0 : theme.border};
    cursor: ${single ? 'auto' : 'pointer'};
  `}
  iframe {
    width: 100%;
    height: 184px;
  }
  @media screen and (min-width: 1024px) {
    iframe {
      width: 100%;
      height: 384px;
    }
  }
`;

const Header = styled(Flex)`
  align-items: center;
  ${({ single }) =>
    !single &&
    css`
      &:hover {
        background: ${({ theme }) => theme.grays['200']};
      }
    `}
`;

const Avatar = styled.div`
  ${({ isExpanded, single, theme }) => {
    const size = single ? 96 : isExpanded ? 48 : 32;
    return css`
      transition: 0.2s ease-in;
      width: ${size}px;
      height: ${size}px;
      font-size: ${size}px;
      background: ${theme.grays['100']};
      display: block;
      border-radius: 50%;
      overflow: hidden;
      text-align: center;
    `;
  }}
`;

const Toggle = styled(Text)`
  font-size: 24px;
  color: ${({ theme }) => theme.color.secondary};
  transition: 0.1s;
  ${({ isExpanded }) => css`
    transform: rotate(${isExpanded ? '270' : '90'}deg);
  `}
`;

const Content = styled(Box)``;

const getGraduateSessions = props => {
  const keys = Object.keys(props);
  const sessions = keys.filter(k => props[k] === 'Yes');
  return sessions
    .map(s => {
      const splitSessionKey = s.split('_');
      return capitalize(splitSessionKey[splitSessionKey.length - 1]);
    })
    .join(', ');
};

const GraduateItem = React.memo(
  ({
    single,
    uid,
    name,
    portraitFluid,
    city,
    country,
    phone_numbers,
    state,
    bio,
    bbsh_graduations,
    email_address,
    website,
    skype,
    graduate_is_featured,
    graduate_is_active,
    graduate_is_integration_practitioner,
    graduate_street_address,
    graduate_postal_code,
    graduate_skype,
    graduate_session_phone,
    graduate_session_skype,
    graduate_session_facetime,
    graduate_session_zoom,
    graduate_session_other,
    graduate_language_list,
  }) => {
    const [isExpanded, onExpand] = useState(single ? true : false);
    const badgeKeys = {
      graduate_is_featured,
      graduate_is_integration_practitioner,
      graduate_is_active,
    };
    const availableSessions = `${getGraduateSessions({
      graduate_session_phone,
      graduate_session_skype,
      graduate_session_facetime,
      graduate_session_zoom,
    })}${graduate_session_other ? `, ${graduate_session_other}` : ''}`;
    const sanitizedBio = bio?.replace(iframeRegExp, '');
    return (
      <Wrapper single={single}>
        <Header
          single={single}
          p={3}
          width={1}
          onClick={single ? undefined : () => onExpand(!isExpanded)}
        >
          <Avatar single={single} isExpanded={isExpanded}>
            {portraitFluid ? (
              <Image objectFit="cover" fluid={portraitFluid} />
            ) : (
              <img src={avatarPlaceholder} />
            )}
          </Avatar>
          <Box flex={1}>
            <Flex alignItems="center">
              <Text
                mx={isExpanded ? 3 : 2}
                fontSize={single ? 4 : isExpanded ? 3 : 1}
              >
                {name}
              </Text>
              <GraduateBadges boolFilters={boolFilters} badgeKeys={badgeKeys} />
            </Flex>
            {!!bbsh_graduations.length && (
              <Text
                mx={isExpanded ? 3 : 2}
                style={{ display: 'block' }}
                variant="secondary"
                fontSize={single ? 2 : isExpanded ? 1 : 0}
              >
                {bbsh_graduations.join(' / ')}
              </Text>
            )}
          </Box>
          <Text
            variant="secondary"
            fontSize={single ? 2 : 0}
            display={['none', 'block']}
          >
            {[
              city,
              usAndCanadaStates[country]?.states?.find(s => s.name === state)
                ?.abbreviation || state,
              country,
            ]
              .filter(Boolean)
              .join(', ')}
          </Text>
          {!single && (
            <Toggle isExpanded={isExpanded} ml={3}>
              ›
            </Toggle>
          )}
        </Header>
        {isExpanded && (
          <Content p={4} m={-1}>
            {website && (
              <PropertyPair p={1} property="Website">
                <a href={website} target="_blank" rel="noopener noreferrer">
                  {website}
                </a>
              </PropertyPair>
            )}
            {email_address && (
              <PropertyPair p={1} property="Email">
                <a href={`mailto:${email_address}`}>{email_address}</a>
              </PropertyPair>
            )}
            {!!phone_numbers?.length && (
              <PropertyPair p={1} property="Phone">
                {phone_numbers.join(' / ')}
              </PropertyPair>
            )}
            {!!graduate_language_list?.length && (
              <PropertyPair p={1} property="Session languages">
                {graduate_language_list
                  ?.map(({ graduate_language }) => graduate_language)
                  .join(' / ')}
              </PropertyPair>
            )}
            {graduate_postal_code && (
              <PropertyPair p={1} property="Postal code">
                {graduate_postal_code}
              </PropertyPair>
            )}
            {graduate_street_address && (
              <PropertyPair p={1} property="Street address">
                {graduate_street_address}
              </PropertyPair>
            )}
            {city && (
              <PropertyPair p={1} property="City">
                {city}
              </PropertyPair>
            )}
            {state && (
              <PropertyPair p={1} property="State">
                {state}
              </PropertyPair>
            )}
            {country && (
              <PropertyPair p={1} property="Country">
                {country}
              </PropertyPair>
            )}
            {graduate_skype && (
              <PropertyPair p={1} property="Skype">
                {skype}
              </PropertyPair>
            )}
            {availableSessions && (
              <PropertyPair p={1} property="Remote sessions via">
                {availableSessions}
              </PropertyPair>
            )}

            {(graduate_is_featured || graduate_is_integration_practitioner) &&
              !single && (
                <Link to={`/graduate/${uid}`}>
                  <Button py={2} px={3} variant="outline" m={1} mt={3}>
                    More about {name} →
                  </Button>
                </Link>
              )}
            {single && bio && (
              <Html mt={4} dangerouslySetInnerHTML={{ __html: sanitizedBio }} />
            )}
          </Content>
        )}
      </Wrapper>
    );
  },
);

GraduateItem.displayName = 'GraduateItem';

GraduateItem.propTypes = {};

export default GraduateItem;
